"use strict";

const AssetRoutineMaintenanceController = function () {
  const htmlPath = "modals/assetRoutineMaintenance";
  const formKey = "outfall-routine-maintenance";
  const issueSectionNames = [
    "structuralConditionIssues",
    "structuralConditionInletIssues",
    "structuralConditionOutletIssues",
    "activeMaintenanceIssues",
  ];

  let $modal;
  let tabs;
  let dataStore;

  var showAssetRoutineMaintenanceModal = async function (newDataStore, readOnly) {
    dataStore = newDataStore;
    const initialData = dataStore.getInitial();

    tabs = getTabs(initialData);
    const renderData = mapTabProps(tabs);

    $modal = TabbedModal.showTabbedModal(formKey, renderData, tabs, htmlPath, {
      readOnly,
      extraButtons: [
        { text: "Preview", type: "primary", class: "online-only", callback: downloadPreviewPdf },
      ],
    });

    initializeForm();
    setInspectionHeaderText(initialData);
    setAdditionalModalHandlers();
    await highlightRequiredFields(initialData.assetInfo);
  };

  var highlightRequiredFields = async function (assetInfo) {
    const assetTypeConfig = AssetTypeConfig.get(assetInfo.assetType);
    if (assetTypeConfig.highlightRequiredFields) {
      return await assetTypeConfig.highlightRequiredFields(
        $modal,
        dataStore.getWithUpdates(),
        assetInfo,
      );
    }

    const data = dataStore.getWithUpdates();
    CommonModalFunctions.highlightEmptyFormFields(
      $modal,
      assetInfo.inventoryData.requiredFields ?? [],
      { ...data, assetInfo: { ...assetInfo.inventoryData, ...data.assetInfo } },
      {},
      ["assetInfo"],
    );
  };

  var getTabs = function (data) {
    const GeneralInfo = require("./assetRoutineMaintenanceGeneralInfo");
    const Condition = require("./assetRoutineMaintenanceCondition");
    const Repair = require("./assetRoutineMaintenanceRepair");
    const Measurements = require("./assetRoutineMaintenanceMeasurements");
    const Sample = require("./assetRoutineMaintenanceSample");
    const Submission = require("./assetRoutineMaintenanceSubmission");
    const tabs = [new GeneralInfo(module.exports)];

    if (data.activeMaintenanceIssues?.length) {
      tabs.push(Repair);
    }

    const config = AssetTypeConfig.get(data.assetInfo.assetType);
    if (AssetRoutineMaintenanceData.supportsStructuralCondition(config)) {
      tabs.push(Condition);
    }

    tabs.push(
      new Measurements(module.exports),
      new Sample(module.exports),
      new Submission(module.exports),
    );

    return tabs;
  };

  var mapTabProps = function (tabs) {
    const data = dataStore.getWithUpdates();
    const tabProps = tabs.reduce((props, tab) => {
      return $.extend(true, props, tab.getProps(data));
    }, {});

    return {
      ...data,
      ...tabProps,
    };
  };

  var getHeaderHtml = function (initialData) {
    return nunjucks.render("modals/assetRoutineMaintenance/assetInspectionHeader.njk", {
      assetInfo: initialData.assetInfo,
    });
  };

  var setInspectionHeaderText = function (initialData) {
    TabbedModal.setHeaderText(getHeaderHtml(initialData));
  };

  var setAdditionalModalHandlers = function () {
    TabbedModal.setCancelHandler(dataStore.discard);
    TabbedModal.setSaveHandler(saveButtonClick);
    TabbedModal.setFormHasUpdatesHandler(formHasUpdates);
    TabbedModal.goToPrevUrlOnClose();
  };

  var saveButtonClick = async function () {
    return saveAndCloseModal();
  };

  var getSubmissionResults = function () {
    const initialData = dataStore.getInitial();
    const updatedData = getUpdateData(initialData, dataStore.getForm(), true)?.updates;
    var results = [];

    if (updatedData.inspectionType === "wq-monitoring") {
      const wqMonitoringResult = WqMonitoringResult.getWqMonitoringResultData(
        initialData.assetInfo.name,
        getInspectionRenderData(),
      );
      results.push(wqMonitoringResult);
    } else if (WqMonitoringConstants.isDryWeatherScreeningType(updatedData.inspectionType)) {
      const dryWeatherResult = DryWeatherResult.getDryWeatherResultData(
        initialData.assetInfo.name,
        getInspectionRenderData(),
      );
      results.push(dryWeatherResult);
    }

    if (updatedData.inspectStructuralConditionMaintenance) {
      const routineInspectionResult = MaintenanceResult.getRoutineMaintenanceResultData(
        initialData.assetInfo.name,
        updatedData?.maintenanceIssues,
        updatedData?.assetAccessible,
      );

      results.unshift(routineInspectionResult);
    }

    return results;
  };

  var getUpdateData = function (initial, dataToUpload, isNew) {
    processInspectionFormData(initial, dataToUpload);
    if (isNew) {
      dataToUpload = $.extend(true, {}, initial, dataToUpload);
    }

    AssetTypeConfig.get(initial.assetInfo.assetType).processUpdateData?.(dataToUpload, initial);

    if (!isNew) {
      dataToUpload = mergeIssueIdsIntoUpdates(initial, dataToUpload);
    }

    dataToUpload = RoutineMaintenanceFormTable.combineRoutineMaintenanceIssues(
      dataToUpload,
      issueSectionNames,
    );
    dataToUpload.maintenanceIssues = RoutineMaintenanceFormTable.cleanRoutineMaintenanceIssues(
      dataToUpload.maintenanceIssues,
      !isNew,
    );

    if (isNew) {
      return {
        assetType: initial.assetInfo.assetType,
        updates: dataToUpload,
      };
    } else {
      return {
        id: initial.id,
        assetType: initial.assetInfo.assetType,
        updates: dataToUpload,
      };
    }
  };

  var processInspectionFormData = function (initial, dataToUpload) {
    if (initial.maintenanceType) {
      dataToUpload.maintenanceType = initial.maintenanceType;
    }

    processInspectionAttribute(initial, dataToUpload, "measurements");
    processInspectionAttribute(initial, dataToUpload, "samples");
  };

  var processInspectionAttribute = function (initial, object, path) {
    if (object !== undefined && object[path] !== undefined) {
      cleanInspectionIds(initial[path], object[path]);
      object[path] = Object.values(object[path]);
    }
  };

  var cleanInspectionIds = function (existingData, inspectionData) {
    const existingIds = existingData ? existingData.map((data) => data.id) : [];
    Misc.removeAttributeValues(existingIds, inspectionData, "id");
  };

  var mergeIssueIdsIntoUpdates = function (initial, dataToUpload) {
    for (const sectionName of issueSectionNames) {
      addIssueIds(initial, dataToUpload, sectionName);
    }

    return dataToUpload;
  };

  var addIssueIds = function (initial, updates, key) {
    for (const i in updates[key]) {
      updates[key][i].id = initial[key][i].id;
    }
  };

  var formHasUpdates = function () {
    return !Form.isFormEmpty(formKey);
  };

  var getInspectionRenderData = function () {
    return dataStore.getWithUpdates();
  };

  var getExistingInspectionData = function () {
    return dataStore.getInitial();
  };

  var getFormData = function (path) {
    return Form.getFormDataAtPath(formKey, path);
  };

  var setFormData = function (path, data) {
    return Form.manuallySetFormDataField(formKey, path, data);
  };

  var unsetFormData = function (path) {
    return Form.manuallyUnsetField(formKey, path);
  };

  var downloadPreviewPdf = async function () {
    InspectionPreview.preview(async function () {
      await Form.getReadyToSavePromise(formKey);
      return getUpdateData(dataStore.getInitial(), dataStore.getForm(), dataStore.isNew());
    }, ApiCalls.previewAssetRoutineMaintenancePdf);
  };

  var saveAndCloseModal = async function () {
    await Form.getReadyToSavePromise(formKey);
    await dataStore.save();
    TabbedModal.hide();
  };

  var _getTabs = function () {
    return tabs;
  };

  var initializeForm = function () {
    Form.initializeAndLoadListeners($modal, formKey, { dataStore });
    FormSettings.customizeForm($modal, formKey);

    const initialData = dataStore.getInitial();
    const existingFiles = initialData.files ?? [];
    const newFiles = dataStore.getForm().files;
    Form.initializeDropzone(formKey, $modal, {
      newFiles,
      existingFiles: existingFiles,
      downloadCallback: AssetTypeConfig.get(initialData.assetInfo.assetType).downloadInspectionFile,
    });
  };

  return {
    formKey,
    showAssetRoutineMaintenanceModal,
    getInspectionRenderData,
    getExistingInspectionData,
    getFormData,
    setFormData,
    unsetFormData,
    saveAndCloseModal,
    getSubmissionResults,
    getUpdateData,
    _getTabs,
  };
};

module.exports = AssetRoutineMaintenanceController();

const ApiCalls = require("../../apiCalls");
const AssetRoutineMaintenanceData = require("./assetRoutineMaintenanceData");
const AssetTypeConfig = require("./assetTypeConfig");
const CommonModalFunctions = require("../../modals/commonModalFunctions");
const DryWeatherResult = require("../../general/results/dryWeatherResult");
const Form = require("../../general/form");
const MaintenanceResult = require("../../general/results/maintenanceResult");
const Misc = require("../../misc");
const RoutineMaintenanceFormTable = require("../../general/routineMaintenanceFormTable");
const TabbedModal = require("../../general/tabbedModal");
const FormSettings = require("../../settings/formSettings");
const InspectionPreview = require("../../general/inspectionPreview");
const WqMonitoringResult = require("../../general/results/wqMonitoringResult");
const WqMonitoringConstants = require("../../wq/inspection/wqMonitoringConstants");
